/** Marker attribute set on every <MaskableSeparator/> wrapper. */
export const MASKABLE_SEPARATOR_ATTR = "data-maskable-separator";

/**
 * Collapses field-divider separators the way a delimiter is trimmed from a
 * joined string: a separator is kept only when there is visible content both
 * before and after it, and never two in a row. Leading, trailing and redundant
 * separators (whose surrounding field groups are fully masked) are hidden.
 *
 * Runs directly on the DOM (no React state) so repeated idempotent calls from
 * sibling separators can't loop. A container is any element that has marked
 * separators as direct children; each is collapsed independently, so nesting
 * (tabs, columns, rows) is handled naturally.
 */
export function collapseMaskableSeparators(root: Element | null): void {
  if (!root) return;

  const separators = root.querySelectorAll<HTMLElement>(
    `[${MASKABLE_SEPARATOR_ATTR}]`,
  );
  if (separators.length === 0) return;

  // Group by parent: each flex container is trimmed on its own children.
  const parents = new Set<HTMLElement>();
  separators.forEach((sep) => {
    if (sep.parentElement) parents.add(sep.parentElement);
  });

  parents.forEach(collapseWithinParent);
}

/** Restores every marked separator under `root` to its natural visibility. */
export function resetMaskableSeparators(root: Element | null): void {
  if (!root) return;
  root
    .querySelectorAll<HTMLElement>(`[${MASKABLE_SEPARATOR_ATTR}]`)
    .forEach((sep) => setSeparatorHidden(sep, false));
}

function collapseWithinParent(parent: HTMLElement): void {
  let pending: HTMLElement | null = null;
  let precededByContent = false;

  for (const child of Array.from(parent.children)) {
    const el = child as HTMLElement;

    if (el.hasAttribute(MASKABLE_SEPARATOR_ATTR)) {
      // Two separators in a row: the earlier one is redundant.
      if (pending) setSeparatorHidden(pending, true);
      pending = el;
      continue;
    }

    // A group with no rendered height (all fields masked, empty wrapper) is
    // transparent: it neither satisfies nor resets the pending separator.
    if (!hasRenderedHeight(el)) continue;

    if (pending) {
      setSeparatorHidden(pending, !precededByContent);
      pending = null;
    }
    precededByContent = true;
  }

  // A separator with nothing after it is trailing → hide.
  if (pending) setSeparatorHidden(pending, true);
}

function hasRenderedHeight(el: HTMLElement): boolean {
  return el.getBoundingClientRect().height > 0;
}

function setSeparatorHidden(sep: HTMLElement, hidden: boolean): void {
  sep.style.display = hidden ? "none" : "";
}
